import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ContractorJobLocationDTO, ContractorJobSummaryDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { ErrorState, Loading } from '../../components/ui/Feedback';

export const CONTRACTOR_JOBS_KEY = ['contractor', 'jobs'] as const;

/** "123 Chorro St, San Luis Obispo · Unit A" */
export function locationLabel(loc: ContractorJobLocationDTO): string {
  const street = [loc.line1, loc.line2].filter(Boolean).join(' ');
  const cityLine = [loc.city, loc.state].filter(Boolean).join(', ');
  return [street || loc.propertyName, cityLine, loc.unitLabel ? `Unit ${loc.unitLabel}` : null]
    .filter(Boolean)
    .join(' · ');
}

function JobCard({ job }: { job: ContractorJobSummaryDTO }) {
  return (
    <li className="card job-card">
      <Link to={`/contractor/jobs/${encodeURIComponent(job.id)}`} className="job-card__link">
        <span className="job-card__title">{job.title}</span>
        <span className="row" style={{ gap: '0.35rem' }}>
          <span className="badge">{job.status}</span>
          {job.priority === 'emergency' || job.priority === 'urgent' ? (
            <span className="badge badge--warn">{job.priority}</span>
          ) : null}
          <span className="muted">{job.category}</span>
        </span>
        <span className="muted">{locationLabel(job.location)}</span>
        {job.scheduledEntryStart && (
          <span>Entry: {formatDateTime(job.scheduledEntryStart)}</span>
        )}
      </Link>
    </li>
  );
}

/** The contractor's job list (mobile-first cards; open by default). */
export function ContractorJobs() {
  const [filter, setFilter] = useState<'open' | 'closed'>('open');
  const query = useQuery({
    queryKey: [...CONTRACTOR_JOBS_KEY, filter],
    queryFn: () => api.contractor.jobs({ status: filter }),
  });

  return (
    <>
      <h1>My jobs</h1>
      <div className="row" role="group" aria-label="Show jobs">
        <button
          type="button"
          className={filter === 'open' ? undefined : 'secondary'}
          aria-pressed={filter === 'open'}
          onClick={() => setFilter('open')}
        >
          Open
        </button>
        <button
          type="button"
          className={filter === 'closed' ? undefined : 'secondary'}
          aria-pressed={filter === 'closed'}
          onClick={() => setFilter('closed')}
        >
          Closed
        </button>
      </div>

      {query.isLoading ? (
        <Loading label="Loading jobs…" />
      ) : query.isError || !query.data ? (
        <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />
      ) : query.data.jobs.length === 0 ? (
        <p className="muted">{filter === 'open' ? 'No open jobs right now.' : 'No closed jobs yet.'}</p>
      ) : (
        <ul className="job-list">
          {query.data.jobs.map((j) => (
            <JobCard key={j.id} job={j} />
          ))}
        </ul>
      )}
    </>
  );
}
